import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { KeyRound, Plus, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState, StatusBadge } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { UserAccessDialog } from "../../components/UserAccessDialog";
import { ResetPasswordDialog } from "../admin/UsersPage";
import { PASSWORD_RULES_HINT } from "../../components/ForgotPasswordDialog";

const emptyForm = { name: "", email: "", mobile: "", password: "", employee_id: "", designation: "" };

// My Team — parents create and manage their own sub-users (same role, inherited access).
// Shared by the Supervisor console and the Collector app.
export default function TeamPage() {
  const { code } = useParams();
  const [data, setData] = useState(null); // {me, sub_users}
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(() => api.get(`/t/${code}/users/my-team`).then((r) => setData(r.data)), [code]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!data?.me?.id) return;
    setBusy(true);
    try {
      await api.post(`/t/${code}/users/${data.me.id}/sub-users`, form);
      toast.success(`Sub-user created — role locked to ${(data.me.role || "").replace(/_/g, " ")}, your menu access inherited`);
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      const r = await api.put(`/t/${code}/users/${u.id}/toggle`);
      toast.success(r.data.is_active ? `${staffLabelText(u.name, u.employee_id)} activated` : `${staffLabelText(u.name, u.employee_id)} deactivated`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!data) return <Spinner />;
  const roleLabel = (data.me.role || "").replace(/_/g, " ");

  return (
    <div className="space-y-4" data-testid="team-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold font-heading flex items-center gap-2"><Users className="h-5 w-5" /> My Team</h1>
          <p className="text-sm text-muted-foreground">
            Sub-users work under you with the same role ({roleLabel}) and inherit your menu access.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="team-add-sub-user-button"><Plus className="h-4 w-4 mr-2" /> Add Sub User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">New sub-user under you</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2">
                Role is locked to <span className="font-medium capitalize">{roleLabel}</span>.
                Your current menu access is copied to the sub-user (editable later).
              </p>
              <div className="space-y-1"><Label>Full name</Label><Input value={form.name} onChange={set("name")} required data-testid="team-sub-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="team-sub-email-input" /></div>
                <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} maxLength={10} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Employee ID</Label><Input value={form.employee_id} onChange={set("employee_id")} /></div>
                <div className="space-y-1"><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} /></div>
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={set("password")} required data-testid="team-sub-password-input" />
                <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="team-sub-submit-button">
                {busy ? "Creating..." : "Create sub-user"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {data.sub_users.length === 0 ? (
        <EmptyState title="No sub-users yet" subtitle="Create a sub-user to delegate your field work — they get your role and menu access." />
      ) : (
        <div className="space-y-3" data-testid="team-sub-users-list">
          {data.sub_users.map((u) => (
            <Card key={u.id} className="shadow-sm">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-2"><StaffLabel code={code} userId={u.id} name={u.name} empCode={u.employee_id} />
                    <Badge variant="outline" className="text-[10px] capitalize">{(u.role || "").replace(/_/g, " ")}</Badge>
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{u.email}{u.mobile ? ` • ${u.mobile}` : ""}</p>
                  <p className="text-xs text-muted-foreground">Added {fmtDate(u.created_at)}{u.designation ? ` • ${u.designation}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={u.is_active ? "active" : "inactive"} />
                  <Switch checked={!!u.is_active} onCheckedChange={() => toggle(u)} data-testid={`team-toggle-${u.email}`} />
                  <Button variant="outline" size="sm" onClick={() => setAccessTarget(u)} data-testid={`team-access-${u.email}`}>
                    <ShieldCheck className="h-4 w-4 mr-1.5" /> Menu access
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Reset password"
                    onClick={() => setResetTarget(u)} data-testid={`team-reset-${u.email}`}>
                    <KeyRound className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UserAccessDialog code={code} user={accessTarget} open={!!accessTarget}
        onOpenChange={(v) => !v && setAccessTarget(null)} onSaved={load} />
      <ResetPasswordDialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}
        targetLabel={resetTarget ? `${staffLabelText(resetTarget.name, resetTarget.employee_id)} · ${resetTarget.email}` : ""}
        onSubmit={async (pw) => {
          const r = await api.post(`/t/${code}/users/${resetTarget.id}/reset-password`, { new_password: pw });
          toast.success(r.data.message || "Password reset");
        }} />
    </div>
  );
}
